// src/components/ui/ItemIngredientsManager.tsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    FiPlus,
    FiX,
    FiPackage,
    FiLayers,
    FiChevronDown,
    FiChevronUp,
    FiSearch
} from 'react-icons/fi';
import Button from './Button';
import InputField from './InputField';
import Badge from './Badge';
import {
    Ingredient,
    GroupWithIngredients,
    ItemIngredientWithDetails,
    ItemIngredientsState
} from '../../types/ingredient';
import { ingredientService } from '../../services/ingredientService';

interface ItemIngredientsManagerProps {
    itemId?: string;
    branchId: string;
    value: ItemIngredientsState;
    onChange: (value: ItemIngredientsState) => void;
    className?: string;
}

const ItemIngredientsManager: React.FC<ItemIngredientsManagerProps> = ({
    itemId,
    branchId,
    value,
    onChange,
    className = ''
}) => {
    const { t, i18n } = useTranslation();

    // Helper function to get display name based on language
    const getDisplayName = (item: Ingredient | GroupWithIngredients): string => {
        if (i18n.language === 'ar' && 'ar' in item && item.ar) {
            // Handle string format for Arabic (from Supabase) - only for ingredients
            if (typeof item.ar === 'string') {
                return item.ar || item.name;
            }
        }
        return item.name;
    };

    // State
    const [loading, setLoading] = useState(true);
    const [ingredients, setIngredients] = useState<Ingredient[]>([]);
    const [groups, setGroups] = useState<GroupWithIngredients[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState<'ingredients' | 'groups'>('ingredients');
    const [isExpanded, setIsExpanded] = useState(false);

    // Load data
    useEffect(() => {
        loadData();
    }, [branchId]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [ingredientsData, groupsData] = await Promise.all([
                ingredientService.getIngredientsByBranch(branchId),
                ingredientService.getGroupsByBranch(branchId)
            ]);
            setIngredients(ingredientsData);
            setGroups(groupsData);
        } catch (error) {
            console.error('Error loading ingredient data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Add ingredient
    const addIngredient = (ingredientId: string) => {
        if (!value.individual_ingredients.includes(ingredientId)) {
            onChange({
                ...value,
                individual_ingredients: [...value.individual_ingredients, ingredientId]
            });
        }
        setSearchTerm('');
    };

    // Remove ingredient
    const removeIngredient = (ingredientId: string) => {
        onChange({
            ...value,
            individual_ingredients: value.individual_ingredients.filter(id => id !== ingredientId)
        });
    };

    // Add group
    const addGroup = (groupId: string) => {
        if (!value.ingredient_groups.includes(groupId)) {
            onChange({
                ...value,
                ingredient_groups: [...value.ingredient_groups, groupId]
            });
        }
        setSearchTerm('');
    };

    // Remove group
    const removeGroup = (groupId: string) => {
        onChange({
            ...value,
            ingredient_groups: value.ingredient_groups.filter(id => id !== groupId)
        });
    };

    // Get filtered items based on search and tab
    const getFilteredItems = () => {
        const searchTermLower = searchTerm.toLowerCase();

        if (activeTab === 'ingredients') {
            return ingredients
                .filter(ingredient => {
                    const englishMatch = ingredient.name.toLowerCase().includes(searchTermLower);
                    const arabicMatch = ingredient.ar &&
                        (typeof ingredient.ar === 'string' ? ingredient.ar.toLowerCase().includes(searchTermLower) : false);
                    return (englishMatch || arabicMatch) && !value.individual_ingredients.includes(ingredient.id);
                })
                .slice(0, 10);
        } else {
            return groups
                .filter(group => {
                    const englishMatch = group.name.toLowerCase().includes(searchTermLower);
                    // Groups don't have Arabic names, only search in English name
                    return englishMatch && !value.ingredient_groups.includes(group.id);
                })
                .slice(0, 10);
        }
    };

    // Get selected ingredients for display
    const selectedIngredients = ingredients.filter(ingredient =>
        value.individual_ingredients.includes(ingredient.id)
    );

    // Get selected groups for display
    const selectedGroups = groups.filter(group =>
        value.ingredient_groups.includes(group.id)
    );

    // No confirm needed - changes apply immediately

    if (loading) {
        return (
            <div className={`space-y-4 ${className}`}>
                <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                    <div className="h-10 bg-gray-200 rounded"></div>
                </div>
            </div>
        );
    }

    return (
        <div className={`space-y-6 ${className}`}>
            <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('menuItems.ingredientsAndGroups')}
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                    {t('menuItems.addIngredientsOrGroups')}
                </p>

                {/* Selected Items Display */}
                <div className="space-y-4 mb-6">
                    {selectedIngredients.length > 0 && (
                        <div className=" border border-orange-200 dark:border-orange-700 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-6 h-6 bg-orange-500 rounded-lg flex items-center justify-center">
                                    <FiPackage className="w-3 h-3 text-white" />
                                </div>
                                <h4 className="text-sm font-semibold text-orange-800 dark:text-orange-200">
                                    {t('menuItems.individualIngredients')}
                                </h4>
                                <Badge variant="secondary" className="bg-orange-100 dark:bg-orange-800 text-orange-700 dark:text-orange-200 text-xs">
                                    {selectedIngredients.length}
                                </Badge>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {selectedIngredients.map((ingredient) => (
                                    <div
                                        key={ingredient.id}
                                        className="group flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-orange-200 dark:border-orange-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                                    >
                                        <FiPackage className="w-3 h-3 text-orange-500" />
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            {getDisplayName(ingredient)}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                removeIngredient(ingredient.id);
                                            }}
                                            className="ml-1 p-1 rounded-full hover:bg-red-100 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-all duration-200"
                                        >
                                            <FiX className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Selected Groups */}
                    {selectedGroups.length > 0 && (
                        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border border-purple-200 dark:border-purple-700 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-6 h-6 bg-purple-500 rounded-lg flex items-center justify-center">
                                    <FiLayers className="w-3 h-3 text-white" />
                                </div>
                                <h4 className="text-sm font-semibold text-purple-800 dark:text-purple-200">
                                    {t('menuItems.ingredientGroups')}
                                </h4>
                                <Badge variant="secondary" className="bg-purple-100 dark:bg-purple-800 text-purple-700 dark:text-purple-200 text-xs">
                                    {selectedGroups.length}
                                </Badge>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {selectedGroups.map((group) => (
                                    <div
                                        key={group.id}
                                        className="group flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-purple-200 dark:border-purple-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200"
                                    >
                                        <FiLayers className="w-3 h-3 text-purple-500" />
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                            {getDisplayName(group)}
                                        </span>
                                        <Badge variant="secondary" className="text-xs px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 border-purple-300 dark:border-purple-600">
                                            {group.ingredient_count || 0}
                                        </Badge>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                removeGroup(group.id);
                                            }}
                                            className="ml-1 p-1 rounded-full hover:bg-red-100 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-all duration-200"
                                        >
                                            <FiX className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {selectedIngredients.length === 0 && selectedGroups.length === 0 && (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                            <FiPackage className="mx-auto h-12 w-12 mb-3 opacity-50" />
                            <p className="text-sm font-medium mb-1">{t('menuItems.noIngredientsYet')}</p>
                            <p className="text-xs opacity-75">{t('menuItems.addIngredientsOrGroups')}</p>
                        </div>
                    )}
                </div>

                {/* Expandable Selector Section */}
                <div className="border-2 border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800 shadow-sm">
                    {/* Header - Click to expand/collapse */}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsExpanded(!isExpanded);
                        }}
                        className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 hover:from-blue-100 hover:to-indigo-100 dark:hover:from-blue-900/30 dark:hover:to-indigo-900/30 transition-all duration-200 border-b border-gray-200 dark:border-gray-700"
                    >
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                                <FiPlus className="w-4 h-4 text-white" />
                            </div>
                            <span className="font-semibold text-gray-900 dark:text-white">
                                {t('menuItems.addIngredientsOrGroupsButton')}
                            </span>
                        </div>
                        <div className={`transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}>
                            <FiChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        </div>
                    </button>

                    {/* Content - Show when expanded */}
                    {isExpanded && (
                        <div className="p-6 bg-gray-50 dark:bg-gray-900/50 space-y-6">
                            {/* Tabs */}
                            <div className="flex space-x-1 bg-white dark:bg-gray-800 rounded-xl p-1.5 shadow-sm border border-gray-200 dark:border-gray-700">
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setActiveTab('ingredients');
                                    }}
                                    className={`flex-1 flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-100 ${activeTab === 'ingredients'
                                        ? 'bg-orange-500 text-white shadow-md'
                                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                                        }`}
                                >
                                    <FiPackage className="w-4 h-4 mr-2" />
                                    {t('menuItems.ingredients')}
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setActiveTab('groups');
                                    }}
                                    className={`flex-1 flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-100 ${activeTab === 'groups'
                                        ? 'bg-purple-500 text-white shadow-md'
                                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                                        }`}
                                >
                                    <FiLayers className="w-4 h-4 mr-2" />
                                    {t('menuItems.groups')}
                                </button>
                            </div>

                            {/* Search */}
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <FiSearch className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder={t('common.ingredientManagement.searchIngredients')}
                                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl 
                                             placeholder-gray-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-white 
                                             focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent
                                             shadow-sm transition-all duration-200"
                                />
                            </div>

                            {/* Items List */}
                            <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                                {getFilteredItems().map((item) => (
                                    <button
                                        key={item.id}
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (activeTab === 'ingredients') {
                                                addIngredient(item.id);
                                            } else {
                                                addGroup(item.id);
                                            }
                                        }}
                                        className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-white to-gray-50 
                                                 dark:from-gray-800 dark:to-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 
                                                 hover:from-orange-50 hover:to-amber-50 dark:hover:from-orange-900/20 dark:hover:to-amber-900/20 
                                                 hover:border-orange-300 dark:hover:border-orange-600 transition-all duration-200 
                                                 hover:shadow-lg text-left group"
                                    >
                                        <div className="flex items-center space-x-3">
                                            <div className={`p-2 rounded-lg transition-colors duration-200 ${activeTab === 'ingredients'
                                                ? 'bg-orange-100 dark:bg-orange-900/30 group-hover:bg-orange-200 dark:group-hover:bg-orange-900/50'
                                                : 'bg-purple-100 dark:bg-purple-900/30 group-hover:bg-purple-200 dark:group-hover:bg-purple-900/50'
                                                }`}>
                                                {activeTab === 'ingredients' ? (
                                                    <FiPackage className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                                                ) : (
                                                    <FiLayers className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                                                )}
                                            </div>
                                            <div>
                                                <div className="font-semibold text-gray-900 dark:text-white text-base">
                                                    {getDisplayName(item)}
                                                </div>
                                                {activeTab === 'groups' && 'ingredient_count' in item && (
                                                    <div className="text-sm text-gray-600 dark:text-gray-400">
                                                        {item.ingredient_count || 0} {t('ingredientManagement.ingredients')}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-2 rounded-full 
                                                      group-hover:from-orange-600 group-hover:to-amber-600 transition-all duration-200 
                                                      transform group-hover:scale-110 shadow-lg">
                                            <FiPlus className="h-4 w-4 text-white" />
                                        </div>
                                    </button>
                                ))}

                                {getFilteredItems().length === 0 && (
                                    <div className="text-center py-12">
                                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 
                                                      rounded-xl p-8 border border-gray-200 dark:border-gray-600">
                                            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-gray-200 to-gray-300 
                                                          dark:from-gray-600 dark:to-gray-500 rounded-full flex items-center justify-center">
                                                {activeTab === 'ingredients' ? (
                                                    <FiPackage className="w-8 h-8 text-gray-500 dark:text-gray-400" />
                                                ) : (
                                                    <FiLayers className="w-8 h-8 text-gray-500 dark:text-gray-400" />
                                                )}
                                            </div>
                                            <p className="text-gray-600 dark:text-gray-300 font-medium text-lg">
                                                {searchTerm
                                                    ? (activeTab === 'ingredients' 
                                                        ? t('menuItems.noIngredientsFound') 
                                                        : t('menuItems.noGroupsFound'))
                                                    : (activeTab === 'ingredients' 
                                                        ? t('menuItems.noAvailableIngredients') 
                                                        : t('menuItems.noAvailableGroups'))
                                                }
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ItemIngredientsManager;